'use client'

import {useTranslate} from '@local/hooks'
import {LinkInput, SanityImageType} from '@local/types'
import {Button, Container, SanityImage, SlideshowNative} from '@local/ui/components'
import {cx} from '@local/utils/cx'
import {useMemo, useState} from 'react'

export interface BookingGridSlideshowProps {
	categories: Array<{
		title: string
		cards: Array<{
			title: string
			price: string
			cta: LinkInput
			image: SanityImageType
			buttonTitle?: string
		}>
	}>
}

export const BookingGridSlideshow = ({categories}: BookingGridSlideshowProps) => {
	const headings = useMemo(() => categories.map((item) => ({title: item.title, count: item.cards.length})), [categories])
	const [activeIndex, setActiveIndex] = useState<number>(0)
	const translate = useTranslate()
	return (
		<>
			{!!headings.length && (
				<Container className='flex flex-row items-start gap-22 lg:gap-8 pt-50 pb-20'>
					{headings.map((item, index) => (
						<Button
							key={index}
							onClick={() => setActiveIndex(index)}
							className={cx(
								'inline-flex items-start',
								'transition-opacity duration-300 ease-expo-out',
								activeIndex === index ? 'opacity-100' : 'opacity-30 hover:opacity-50',
							)}>
							<h2
								className={cx(
									'font-sans uppercase text-34 lg:text-105 leading-snug -tracking-wider font-medium',
								)}>
								{item.title}
							</h2>
							<span className='font-serif text-14 lg:text-24 pt-3 pl-3 lg:px-5 lg:pt-20'>
								({item.count})
							</span>
						</Button>
					))}
				</Container>
			)}
			<div className='realtive h-auto'>
				{!!categories.length && categories.map((category, index) => (
					<SlideshowNative
						key={`slideshow-${index}`}
						withDotNavigation={false}
						withPrevNextNavigation
						aria-hidden={activeIndex !== index}
						className={cx(
							'margin-px-1 gap-20 lg:gutter-gap-1 scroll-pl-[var(--margin)]',
						)}
						containerClassName={cx(
							'w-full h-fit',
							'transform transition-opacity duration-500',
							index === 0 ? 'relative' : 'absolute h-fit -translate-y-full',
							activeIndex === index ? 'opacity-100 delay-[250ms] ease-expo-out z-1' : 'opacity-0 ease-expo-out z-0',
						)}
						prevNextNavigationClassName={cx(
							'lg-max:hidden',
							'absolute bottom-full margin-right-1 mb-45',
							'[&>button]:w-44 [&>button]:h-44',
						)}>
						{category.cards.map((card, cardIndex) => (
							<Button
								key={`slideshow-${index}-card-${cardIndex}`}
								linkInput={card.cta}
								className={cx(
									'w-320 lg:span-w-3 relative group/item h-fit',
								)}>
								<figure className='relative w-full aspect-[320/420] lg:aspect-[335/500] rounded-lg overflow-clip'>
									<SanityImage
										image={card.image}
										width={335}
										height={500}
										desktopWidth={335}
										mobileWidth={320}
										className='w-full h-full object-cover' />
									<Button
										className={cx(
											'absolute w-[calc(100%-2*0.75rem)] z-1 left-12 right-12 bottom-12',
											'transition-opacity ease-expo-out opacity-0 duration-300 lg-max:opacity-100',
											'group-hover/item:opacity-100',
										)}
										variant='primary'>
										{card?.buttonTitle ?? translate('Book Now')}
									</Button>
								</figure>
								<div className='flex flex-row justify-between pt-16'>
									<h3 className='font-serif text-24'>{card.title}</h3>
									<span className='self-end font-sans uppercase text-10 text-grey-2'>{card.price}</span>
								</div>
							</Button>
						))}
					</SlideshowNative>
				))}
			</div>
		</>
	)
}
